import React, { useState, useRef, useEffect } from 'react';
import { Card, CardBody, Input, Button, Select, SelectItem } from '@nextui-org/react';
import HeaderComponent from '../../components/General/HeaderComponent';
import QRCodeStyling from 'qr-code-styling';
import { BsQrCode } from 'react-icons/bs';
import { FiDownload, FiCopy, FiImage, FiX } from 'react-icons/fi';
import { Notify } from 'notiflix';

export default function QRGenerator() {
  const [text, setText] = useState('');
  const [size, setSize] = useState(300);
  const [level, setLevel] = useState('M'); // L, M, Q, H
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [logoImage, setLogoImage] = useState(null);
  const [logoSize, setLogoSize] = useState(60);
  const [dotsType, setDotsType] = useState('square'); // square, dots, rounded, classy, classy-rounded, extra-rounded
  const [cornerSquareType, setCornerSquareType] = useState('square'); // square, extra-rounded, dot
  const [cornerDotType, setCornerDotType] = useState('square'); // square, dot
  const [margin, setMargin] = useState(0);
  const fileInputRef = useRef(null);
  const qrCodeRef = useRef(null);
  const qrCode = useRef(null);

  // QR Code instance oluştur ve güncelle
  useEffect(() => {
    if (!qrCodeRef.current) return;

    // Instance yoksa oluştur
    if (!qrCode.current) {
      qrCode.current = new QRCodeStyling({
        width: size,
        height: size,
        type: 'canvas',
        data: text || 'https://heypena.com',
        errorCorrectionLevel: level,
        dotsOptions: {
          color: fgColor,
          type: dotsType
        },
        backgroundOptions: {
          color: bgColor
        },
        cornersSquareOptions: {
          color: fgColor,
          type: cornerSquareType
        },
        cornersDotOptions: {
          color: fgColor,
          type: cornerDotType
        },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 10,
          imageSize: logoSize / 100
        },
        margin: margin,
        image: logoImage || undefined
      });

      qrCodeRef.current.innerHTML = '';
      qrCode.current.append(qrCodeRef.current);
    } else {
      // Instance varsa güncelle ve yeniden render et
      qrCode.current.update({
        width: size,
        height: size,
        data: text || 'https://heypena.com',
        errorCorrectionLevel: level,
        dotsOptions: {
          color: fgColor,
          type: dotsType
        },
        backgroundOptions: {
          color: bgColor
        },
        cornersSquareOptions: {
          color: fgColor,
          type: cornerSquareType
        },
        cornersDotOptions: {
          color: fgColor,
          type: cornerDotType
        },
        margin: margin,
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 10,
          imageSize: logoSize / 100
        },
        image: logoImage || undefined
      });

      // Canvas'ı temizleyip yeniden render et
      qrCodeRef.current.innerHTML = '';
      qrCode.current.append(qrCodeRef.current);
    }

    return () => {
      if (qrCodeRef.current) {
        qrCodeRef.current.innerHTML = '';
      }
    };
  }, [text, size, level, fgColor, bgColor, dotsType, cornerSquareType, cornerDotType, margin, logoSize, logoImage]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Notify.failure('Lütfen bir görsel dosyası seçin');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Notify.failure('Logo boyutu 2MB\'dan küçük olmalıdır');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    if (!text.trim()) {
      Notify.warning('Lütfen önce bir metin girin');
      return;
    }

    try {
      const dataUrl = await qrCode.current.getRawData('png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `qrcode-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      Notify.success('QR kod indirildi!');
    } catch (error) {
      console.error('İndirme hatası:', error);
      Notify.failure('QR kod indirilemedi!');
    }
  };

  const handleCopy = () => {
    if (!text.trim()) {
      Notify.warning('Lütfen önce bir metin girin');
      return;
    }

    navigator.clipboard.writeText(text);
    Notify.success('Metin panoya kopyalandı!');
  };

  const dotsTypeOptions = [
    { value: 'square', label: 'Kare' },
    { value: 'dots', label: 'Noktalı' },
    { value: 'rounded', label: 'Yuvarlatılmış' },
    { value: 'classy', label: 'Klasik' },
    { value: 'classy-rounded', label: 'Klasik Yuvarlatılmış' },
    { value: 'extra-rounded', label: 'Ekstra Yuvarlatılmış' }
  ];

  const cornerTypeOptions = [
    { value: 'square', label: 'Kare' },
    { value: 'extra-rounded', label: 'Ekstra Yuvarlatılmış' },
    { value: 'dot', label: 'Nokta' }
  ];

  return (
    <div className="w-full dark:bg-dark-100 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-6">
        <HeaderComponent title="QR Generator" />
        
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Sol Kolon - Form */}
          <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
            <CardBody className="p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300 mb-4">
                  QR Kod Ayarları
                </h2>
                
                <div className="space-y-4">
                  <Input
                    label="Metin / URL"
                    placeholder="QR kod oluşturmak istediğiniz metni veya URL'yi girin"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    fullWidth
                    classNames={{
                      input: "border border-gray-200 dark:border-dark-400",
                      inputWrapper: "border border-gray-200 dark:border-dark-400"
                    }}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Boyut
                      </label>
                      <Input
                        type="number"
                        value={size}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (val >= 100 && val <= 1000) setSize(val);
                        }}
                        min={100}
                        max={1000}
                        classNames={{
                          input: "border border-gray-200 dark:border-dark-400",
                          inputWrapper: "border border-gray-200 dark:border-dark-400"
                        }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Hata Düzeltme Seviyesi
                      </label>
                      <select
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-300 text-[#283252] dark:text-darkWhite-300"
                      >
                        <option value="L">L - Düşük (~7%)</option>
                        <option value="M">M - Orta (~15%)</option>
                        <option value="Q">Q - Yüksek (~25%)</option>
                        <option value="H">H - En Yüksek (~30%)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Ön Plan Rengi
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={fgColor}
                          onChange={(e) => setFgColor(e.target.value)}
                          className="w-16 h-10"
                        />
                        <Input
                          type="text"
                          value={fgColor}
                          onChange={(e) => setFgColor(e.target.value)}
                          placeholder="#000000"
                          classNames={{
                            input: "border border-gray-200 dark:border-dark-400",
                            inputWrapper: "border border-gray-200 dark:border-dark-400"
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Arka Plan Rengi
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          className="w-16 h-10"
                        />
                        <Input
                          type="text"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          placeholder="#FFFFFF"
                          classNames={{
                            input: "border border-gray-200 dark:border-dark-400",
                            inputWrapper: "border border-gray-200 dark:border-dark-400"
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Stil Seçenekleri */}
                  <div className="pt-4 border-t border-gray-200 dark:border-dark-400 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Nokta Stili
                      </label>
                      <Select
                        selectedKeys={[dotsType]}
                        onSelectionChange={(keys) => setDotsType(Array.from(keys)[0])}
                        classNames={{
                          trigger: "border border-gray-200 dark:border-dark-400"
                        }}
                      >
                        {dotsTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                          Köşe Kare Stili
                        </label>
                        <Select
                          selectedKeys={[cornerSquareType]}
                          onSelectionChange={(keys) => setCornerSquareType(Array.from(keys)[0])}
                          classNames={{
                            trigger: "border border-gray-200 dark:border-dark-400"
                          }}
                        >
                          {cornerTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                          Köşe Nokta Stili
                        </label>
                        <Select
                          selectedKeys={[cornerDotType]}
                          onSelectionChange={(keys) => setCornerDotType(Array.from(keys)[0])}
                          classNames={{
                            trigger: "border border-gray-200 dark:border-dark-400"
                          }}
                        >
                          {cornerTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Kenar Boşluğu ({margin}px)
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        value={margin}
                        onChange={(e) => setMargin(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Logo Ayarları */}
                  <div className="pt-4 border-t border-gray-200 dark:border-dark-400 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                        Logo Ekle
                      </label>
                      {logoImage ? (
                        <div className="relative">
                          <div className="flex items-center gap-3 p-3 border border-gray-200 dark:border-dark-400 rounded-lg bg-gray-50 dark:bg-dark-400">
                            <img
                              src={logoImage}
                              alt="Logo"
                              className="w-12 h-12 object-contain rounded"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-[#283252] dark:text-darkWhite-300">
                                Logo yüklendi
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Boyut: {logoSize}%
                              </p>
                            </div>
                            <Button
                              isIconOnly
                              variant="light"
                              size="sm"
                              onPress={handleRemoveLogo}
                            >
                              <FiX className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          variant="bordered"
                          className="w-full"
                          startContent={<FiImage className="w-4 h-4" />}
                          onPress={() => fileInputRef.current?.click()}
                        >
                          Logo Seç
                        </Button>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>

                    {logoImage && (
                      <div>
                        <label className="block text-sm font-medium text-[#283252] dark:text-darkWhite-300 mb-2">
                          Logo Boyutu ({logoSize}%)
                        </label>
                        <input
                          type="range"
                          min="20"
                          max="80"
                          value={logoSize}
                          onChange={(e) => setLogoSize(parseInt(e.target.value))}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>20%</span>
                          <span>50%</span>
                          <span>80%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      startContent={<FiDownload className="w-4 h-4" />}
                      onPress={handleDownload}
                      isDisabled={!text.trim()}
                    >
                      İndir
                    </Button>
                    <Button
                      variant="bordered"
                      className="flex-1"
                      startContent={<FiCopy className="w-4 h-4" />}
                      onPress={handleCopy}
                      isDisabled={!text.trim()}
                    >
                      Kopyala
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Sağ Kolon - QR Kod Önizleme */}
          <Card className="border border-gray-200 dark:border-dark-300 bg-white dark:bg-dark-300 shadow-none">
            <CardBody className="p-6">
              <h2 className="text-lg font-semibold text-[#283252] dark:text-darkWhite-300 mb-4">
                Önizleme
              </h2>
              
              <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-dark-400 rounded-lg min-h-[400px]">
                {text.trim() ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative p-4 bg-white rounded-lg border border-gray-200 dark:border-dark-300 flex items-center justify-center">
                      <div ref={qrCodeRef} className="flex items-center justify-center"></div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-xs break-all">
                      {text}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4 text-gray-400 dark:text-gray-500">
                    <BsQrCode className="w-24 h-24 opacity-30" />
                    <p className="text-center">
                      QR kod önizlemesi için yukarıdaki alana metin veya URL girin
                    </p>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
